/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IModuleStateManager, IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import classname from 'classnames';
import get from 'lodash/get';
import * as _ from 'lodash';
import * as React from 'react';

import { IPaymentContainerData } from './payment-container.data';
import { IPaymentContainerProps } from './payment-container.props.autogenerated';
import LoadingComponent from './components/loading';
import { PaymentProvider, IPaymentTenderType } from './context/payment-context';

import { calculateSurchargeAsync } from '../../actions/DataActionExtension.g';
import { PaymentSelectorButton } from './components/PaymentSelectorButton';
import { observable } from 'mobx';
import { AttributeTextValue, Cart } from '@msdyn365-commerce/retail-proxy';

export interface IPaymentContainerModuleProps extends IPaymentContainerProps<IPaymentContainerData>, IModuleStateProps {
    enableControl: boolean;
}

export interface IPaymentContainerViewProps extends IPaymentContainerProps<IPaymentContainerData> {
    renderSelectorButtons: React.ReactNode;
    renderSelectPaymentLink: React.ReactNode;
    className: string;
    paymentContainerProps: IModuleProps;
    items: any[];
    renderSaveButton: React.ReactNode;
    moduleState: IModuleStateManager;
    selectedPaymentMethod: IPaymentTenderType | null;
}

interface IPaymentContainerState {
    paymentModules: Record<string, IPaymentTenderType[]>;
    selectedPayment: IPaymentTenderType | null;
}

export interface IPaymentContainerItem {
    id: string;
    loading?: React.ReactNode;
    itemProps: INodeProps;
    item: React.ReactNode;
}

enum TenderType {
    'Debit Card' = 'Debit',
    'Future' = 'ACH'
}

// interface ExtendedAttributeValueBase extends AttributeValueBase {
//     TextValue?: string;
// }

/**
 *  Enables the ability to select a payment method from the checkout page and runs surcharge logic when a payment method is chosen.
 * PaymentContainer component
 * @extends {React.PureComponent<IPaymentContainerModuleProps>}
 */
export class PaymentContainer extends React.PureComponent<IPaymentContainerModuleProps, IPaymentContainerState> {
    /**
     * Locks the surcharge processing while this is true.
     * @private
     */
    @observable private isSurchargeProcessing: boolean = false;

    @observable private hasSurchargeProcessed: boolean = false;

    @observable private hasPaymentAttributeUpdated: boolean = false;

    public constructor(props: IPaymentContainerModuleProps) {
        super(props);
        this.registerPaymentMethod = this.registerPaymentMethod.bind(this);
        this.unsetPaymentMethod = this.unsetPaymentMethod.bind(this);
        this.setSelectedPayment = this.setSelectedPayment.bind(this);
        this.calculateSurcharge = this.calculateSurcharge.bind(this);
        this.state = { paymentModules: {}, selectedPayment: null };
    }

    /**
     * Component did mount lifecycle method.
     */
    public componentDidMount(): void {
        const paymentModules = this.getPaymentModules();
        const childIds = paymentModules.map((item: React.ReactNode) => get(item, 'props.id') || '');
        this.props.moduleState.init({
            childIds
        });
    }

    /**
     * Registers a payment method into the page state. Called through the Payment Context
     * @param elementId Name of the element
     * @param tenderTypeList Tender type
     * @private
     */
    private registerPaymentMethod(elementId: string, tenderTypeList: IPaymentTenderType[]) {
        // const tempMethods = { ...this.paymentModules, [elementId]: tenderTypeList };
        // this.paymentModules = tempMethods;
        this.setState(prevState => ({ paymentModules: { ...prevState.paymentModules, [elementId]: tenderTypeList } }));
    }

    /**
     * Gets payment modules from the slots.
     */
    private readonly getPaymentModules = (): React.ReactNode[] => {
        const { slots } = this.props;
        const items: React.ReactNode[] = [];
        if (slots && slots.primary && slots.primary.length > 0) {
            items.push(...slots.primary);
        }
        return items;
    };

    /**
     * Gets the active child module states
     * @returns module state manager
     */
    private readonly getActiveChildModuleStates = (): IModuleStateManager[] => {
        const { childIds, getModule } = this.props.moduleState;
        return childIds
            .map(getModule)
            .filter((state): state is IModuleStateManager => !!state && !state.isDisabled && state.hasModuleState);
    };

    /**
     * Converts the Record<string, IPaymentTenderType[]> to a flat array of IPaymentTenderType[]
     * @returns Array of IPaymentTenderType
     */
    private getPaymentModulesAsArray = (): IPaymentTenderType[] =>
        // eslint-disable-next-line security/detect-object-injection
        _.flatten(Object.keys(this.state.paymentModules).map(entry => this.state.paymentModules[entry]));

    /**
     * Renders the payment select buttons based on the registered modules
     * @returns React element
     */
    public renderSelectorButtons = (isOnAccountCust: boolean, isAccountBlocked: boolean): React.ReactNode => {
        const onAccountText = 'ON ACCOUNT';
        const containsInvoice = this.props.data.checkout.result?.checkoutCart.hasInvoiceLine;
        const paymentModulesAsArray = this.getPaymentModulesAsArray();
        // Customer is able to pay on account only if the On Account payment module exists in payment modules state
        // const isOnAccountCust = paymentModulesAsArray.find(p => p.tenderName.toUpperCase() === onAccountText);
        const filteredPaymentModules = paymentModulesAsArray.filter(p => {
            if (isOnAccountCust) {
                if (!containsInvoice) {
                    return p.tenderName.toUpperCase() === onAccountText;
                }
            }
            return p.tenderName.toUpperCase() !== onAccountText;
        });

        return (
            <div className={`payment-selector__button-container ${this.state.selectedPayment ? 'hide-buttons' : ''}`}>
                {filteredPaymentModules.map(option => (
                    <PaymentSelectorButton
                        paymentTenderType={option}
                        onTenderTypeSelect={this.setSelectedPayment}
                        key={option.tenderTypeId}
                        disabled={isOnAccountCust && isAccountBlocked}
                    />
                ))}
            </div>
        );
    };

    /**
     * Unsets the selected payment method
     */
    private unsetPaymentMethod = () => {
        this.setState({ selectedPayment: null });
        this.hasSurchargeProcessed = false;
    };

    /**
     * Renders the link to change payment methods when one is selected
     */
    public renderSelectPaymentLink = (): React.ReactNode => {
        const { selectPaymentLinkText } = this.props.resources;
        return (
            <div className={`payment-selector__select-payment-container ${this.state.selectedPayment ? '' : 'hide-buttons'}`}>
                <button className='payment-selector__select-payment-container--link msc-btn btn-link' onClick={this.unsetPaymentMethod}>
                    {selectPaymentLinkText}
                </button>
            </div>
        );
    };

    /**
     * Sets the selected payment method
     * @param paymentType payment Type that has been selected by the user.
     */
    public setSelectedPayment = async (paymentType: IPaymentTenderType): Promise<void> => {
        if (!this.hasPaymentAttributeUpdated || paymentType.tenderTypeId !== this.state.selectedPayment?.tenderTypeId) {
            this.hasSurchargeProcessed = false;
            await (await this.props.data.checkout).checkoutCart.refreshCart({});
            await this.updatePaymentTypeAttribute(paymentType);
        }

        this.setState({ selectedPayment: paymentType });

        if (!this.isSurchargeProcessing) {
            try {
                this.isSurchargeProcessing = true;
                await this.calculateSurcharge();
                const checkout = await this.props.data.checkout;
                const checkoutCart = checkout.checkoutCart;
                if (checkoutCart.hasInvoiceLine) {
                    await this.updatePaymentDetailsAttribute(checkoutCart.cart);
                }
            } finally {
                this.isSurchargeProcessing = false;
            }
        }
    };

    /**
     * Calculate the surcharge for the cart if applicable
     */
    private calculateSurcharge = async () => {
        const cartId = (await this.props.data.checkout).checkoutCart.cart.Id;
        if (this.state.selectedPayment && !this.hasSurchargeProcessed) {
            await calculateSurchargeAsync(
                { callerContext: this.props.context.actionContext },
                cartId,
                +this.state.selectedPayment?.tenderTypeId
            );
            void (await this.props.data.checkout.result?.checkoutCart.refreshCart({}));
            this.hasSurchargeProcessed = true;
        }
    };

    private updatePaymentTypeAttribute = async (paymentType: IPaymentTenderType | null): Promise<void> => {
        if (!paymentType) {
            return;
        }

        const paymentTypeAttribute = {
            '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
            Name: 'PaymentType',
            ExtensionProperties: [],
            TextValue:
                paymentType.tenderName === 'Credit Card' ? null : TenderType[paymentType.tenderName as keyof typeof TenderType] || null,
            TextValueTranslations: []
        };

        const cart = this.props.data.checkout.result?.checkoutCart.cart;
        const attributeArr = [...(cart?.AttributeValues || [])];

        const idx = attributeArr.findIndex(attribute => attribute.Name === 'PaymentType');

        if (idx !== -1) {
            attributeArr.splice(idx, 1, paymentTypeAttribute);
        } else {
            attributeArr.push(paymentTypeAttribute);
        }

        await (await this.props.data.checkout).checkoutCart.updateAttributeValues({ newAttributeValues: attributeArr });
        // await (await this.props.data.checkout).checkoutCart.refreshCart({});
        this.hasPaymentAttributeUpdated = true;
    };

    /**
     * Updates the PaymentDetails attribute in the cart.
     * @param paymentType Selected payment type.
     */
    private updatePaymentDetailsAttribute = async (checkoutCart: Cart): Promise<void> => {
        if (!checkoutCart) {
            return;
        }

        const updatedCartLines =
            checkoutCart.CartLines?.map(cartLine => {
                if (!cartLine.InvoiceId) {
                    return cartLine;
                }

                const existingAttribute: AttributeTextValue[] = cartLine.AttributeValues || [];
                const idx = existingAttribute.findIndex(attr => attr.Name === 'PaymentDetails');

                const paymentDetailsAttribute = {
                    Name: 'PaymentDetails',
                    TextValue: cartLine.ExtendedPrice?.toString(),
                    '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue'
                } as AttributeTextValue;

                if (idx !== -1) {
                    existingAttribute.splice(idx, 1, paymentDetailsAttribute);
                } else {
                    existingAttribute.push(paymentDetailsAttribute);
                }

                return { ...cartLine, AttributeValues: existingAttribute };
            }) || [];

        if (updatedCartLines.length > 0) {
            await (await this.props.data.checkout).checkoutCart.updateCartLines({ updatedCartLinesObject: updatedCartLines });
            // void (await (await this.props.data.checkout).checkoutCart.refreshCart({}));
        }
    };

    /**
     * Handles some sort of click?
     */
    private readonly onClick = async (): Promise<void> => {
        this.getActiveChildModuleStates().forEach(state => {
            !state.isReady && state.onSubmit && state.onSubmit();
        });
        this.onReady();
        if (this.props.data.checkout.result?.shouldEnableSinglePaymentAuthorizationCheckout) {
            await this.props.data.checkout.result.updateShouldCollapsePaymentSection({ newShouldCollapsePaymentSection: true });
        }
    };

    /**
     * Sets the module state to ready.
     */
    private readonly onReady = () => {
        this.props.moduleState.onReady();
    };

    /**
     * Renders the save button
     */
    public renderSaveButton = (): React.ReactNode => {
        const { saveButtonLabel } = this.props.resources;
        // const { moduleState } = this.props;
        // let isSaveButtonHidden = false;
        const element = document.getElementsByClassName('payment-selector__button-container');
        if (element && element[0]) {
            const guidedFormContainer = element[0].closest('.ms-checkout__guided-card-content');
            const saveAndContinueButtonFooter =
                guidedFormContainer && guidedFormContainer.nextSibling ? guidedFormContainer.nextSibling : null;
            // @ts-expect-error
            if (saveAndContinueButtonFooter && saveAndContinueButtonFooter.getElementsByClassName('ms-checkout__guided-card-btn-save')) {
                // @ts-expect-error
                saveAndContinueButtonFooter.classList.add('hide-save-button');
            }
        }
        return (
            <div className={`payment-selector__save-container ${this.state.selectedPayment ? '' : 'hide-buttons'}`}>
                <button className='payment-selector__save-container--save msc-btn btn-primary' onClick={this.onClick}>
                    {saveButtonLabel}
                </button>
            </div>
        );
    };

    public render(): JSX.Element | null {
        const {
            enableControl,
            moduleState,
            config: { width },
            data: { customerInformation }
        } = this.props;

        const items = this.getPaymentModules();
        if (items.length === 0) {
            this.props.context.telemetry.error('Checkout section content is empty, module wont render');
            return null;
        }

        const moduleClassName = classname(
            'ms-checkout-section-container',
            {
                'has-initialized': moduleState.hasInitialized,
                'is-ready': moduleState.isReady,
                'is-updating': !moduleState.isReady,
                container: width === 'container',
                'col-12 fullwidth': width === 'fluid',
                'hide-payments': !this.state.selectedPayment
            },
            this.props.config.className
        );

        const isOnAccountCust = customerInformation.result?.AllowOnAccountPayment || false;
        const isAccountBlocked = customerInformation.result?.Blocked || false;

        const viewProps: IPaymentContainerViewProps = {
            ...this.props,
            selectedPaymentMethod: this.state.selectedPayment,
            renderSelectorButtons: this.renderSelectorButtons(isOnAccountCust, isAccountBlocked),
            renderSelectPaymentLink: this.renderSelectPaymentLink(),
            className: '',
            paymentContainerProps: {
                moduleProps: this.props,
                className: moduleClassName
            },
            items: items.map((item: React.ReactNode) => {
                const childId = get(item, 'props.id') || '';
                const state = moduleState.getModule(childId);

                // Modules have not completed initializing or it's disable
                const hidden = state.hasModuleState && (!state.hasInitialized || state.isDisabled || state.isSkipped);
                return {
                    id: childId,
                    loading: state.hasModuleState && !state.hasInitialized && <LoadingComponent />,
                    itemProps: {
                        'data-type-name': state.typeName,
                        className: classname('ms-checkout-section-container__item', {
                            hidden,
                            initialized: state.hasInitialized,
                            disabled: state.isDisabled,
                            skipped: state.isSkipped,
                            ready: state.isReady,
                            updating: state.isUpdating,
                            pending: state.isPending
                        })
                    },
                    item: React.cloneElement(item as React.ReactElement, { enableControl })
                };
            }),
            renderSaveButton: this.renderSaveButton(),
            moduleState: this.props.moduleState
        };

        // const { setSelectedPaymentState, registerPaymentMethod } = this;
        return (
            <PaymentProvider
                value={{
                    paymentModules: this.state.paymentModules,
                    selectedPayment: this.state.selectedPayment || ({} as IPaymentTenderType),
                    setSelectedPaymentState: this.setSelectedPayment,
                    registerPaymentMethod: this.registerPaymentMethod
                }}
            >
                {this.props.renderView(viewProps)}
            </PaymentProvider>
        );
    }
}

export default withModuleState(PaymentContainer);
